import { queryOptions } from '@tanstack/react-query';
import { PACSCommunicationContext } from 'app/components/PACSCommunicationProvider/context';
import { SearchParams } from 'app/components/PACSQuery/SearchParams';
import { DicomData } from 'app/interfaces/Dicom';
import { FindQueryModel, PACSCommunication } from 'app/interfaces/PACSCommunication';
import { getDicomValue } from 'app/utils/dicom/DicomData';
import { addDays, format } from 'date-fns';
import _ from 'lodash';
import { useContext } from 'react';

type FindStudyResultKeys =
  | 'Modality'
  | 'AccessionNumber'
  | 'StudyDate'
  | 'PatientName'
  | 'PatientID'
  | 'StudyTime'
  | 'PatientSex'
  | 'PatientSexNeutered'
  | 'PatientBirthDate'
  | 'StudyID'
  | 'StudyInstanceUID'
  | 'SOPInstanceUID';

export type FindStudyResult = {
  [key in FindStudyResultKeys]: DicomData;
};

async function fetchStudies(pacsCommunication: PACSCommunication, searchParams?: SearchParams) {
  const { PatientID, PatientName, searchStartDate, searchEndDate, AccessionNumber } =
    searchParams ?? {};
  let searchedDateStr;
  if (searchStartDate || searchEndDate) {
    const startDateStr = searchStartDate ? format(searchStartDate, 'yyyyMMdd') : '';
    const endDateStr = searchEndDate ? format(searchEndDate, 'yyyyMMdd') : '';
    searchedDateStr = `${startDateStr}-${endDateStr}`;
  }

  const queryPromises = await Promise.all([
    pacsCommunication.find(
      {
        QueryRetrieveLevel: 'IMAGE',
        Modality: 'DX',
        AccessionNumber,
        StudyDate: searchedDateStr,
        PatientName,
        PatientID,
        StudyTime: undefined,
        PatientSex: undefined,
        PatientSexNeutered: undefined,
        PatientBirthDate: undefined,
        StudyID: undefined,
        StudyInstanceUID: undefined,
        SOPInstanceUID: undefined,
      },
      FindQueryModel.Study
    ) as Promise<FindStudyResult[]>,
    pacsCommunication.find(
      {
        QueryRetrieveLevel: 'IMAGE',
        Modality: 'CR',
        AccessionNumber,
        StudyDate: searchedDateStr,
        PatientName,
        PatientID,
        StudyTime: undefined,
        PatientSex: undefined,
        PatientSexNeutered: undefined,
        PatientBirthDate: undefined,
        StudyID: undefined,
        StudyInstanceUID: undefined,
        SOPInstanceUID: undefined,
      },
      FindQueryModel.Study
    ) as Promise<FindStudyResult[]>,
    pacsCommunication.find(
      {
        QueryRetrieveLevel: 'IMAGE',
        Modality: 'US',
        AccessionNumber,
        StudyDate: searchedDateStr,
        PatientName,
        PatientID,
        StudyTime: undefined,
        PatientSex: undefined,
        PatientSexNeutered: undefined,
        PatientBirthDate: undefined,
        StudyInstanceUID: undefined,
        SOPInstanceUID: undefined,
      },
      FindQueryModel.Study
    ) as Promise<FindStudyResult[]>,
  ]);

  const retrievedImages = _.flatten(queryPromises);
  const imagesByStudy = _.groupBy(retrievedImages, (image) =>
    getDicomValue(image.StudyInstanceUID)
  );
  const formattedStudies = _.reverse(
    _.sortBy(
      _.map(imagesByStudy, (images) => ({
        images,
      })),
      ({ images }) => `${images[0]?.StudyDate}_${images[0]?.StudyTime}`
    )
  );

  return formattedStudies;
}

export default function queryPACSStudiesQueryOptions(searchParams?: SearchParams) {
  const pacsCommunication = useContext(PACSCommunicationContext);

  return queryOptions({
    queryKey: searchParams
      ? ([{ query: 'pacsFindStudy' }, searchParams] as const)
      : ([{ query: 'pacsFindStudy' }] as const),
    networkMode: 'always',
    queryFn: async ({ queryKey }) => {
      const [, searchParams] = queryKey;
      const defaultDate = addDays(new Date(), -7);
      const noParams = _.isEmpty(_.filter(searchParams));
      const usedSearchParams = noParams ? { searchStartDate: defaultDate } : searchParams;

      return await fetchStudies(pacsCommunication, usedSearchParams);
    },
  });
}
